import type {
  ClientStatus,
  ConversationView,
  LocalActivity,
  LocalMessage,
  PinnedMessage,
  ReceiptState,
  SwChatClient,
  ThreadSummary,
} from '@swchat/client-sdk';
import type { Channel, PersonalPrefs, Project } from '@swchat/protocol';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export const ClientContext = createContext<SwChatClient | null>(null);

export function useClient(): SwChatClient {
  const client = useContext(ClientContext);
  if (!client) throw new Error('ClientContext chưa sẵn sàng');
  return client;
}

export function useStatus(): ClientStatus {
  const client = useClient();
  const [status, setStatus] = useState(client.status);
  useEffect(() => client.on('status', setStatus), [client]);
  return status;
}

export function useConversations(): ConversationView[] {
  const client = useClient();
  const [list, setList] = useState<ConversationView[]>([]);
  // Nhiều lần tải chạy song song có thể xong không theo thứ tự → chỉ nhận kết quả của lần gọi mới nhất.
  const seq = useRef(0);
  const reload = useCallback(() => {
    const n = ++seq.current;
    void client.getConversations().then((views) => n === seq.current && setList(views));
  }, [client]);
  useEffect(() => {
    reload();
    const offs = [client.on('conversations', reload), client.on('messages', reload), client.on('warning', reload)];
    return () => {
      seq.current++;
      offs.forEach((off) => off());
    };
  }, [client, reload]);
  return list;
}

/**
 * Tin của hội thoại (200 tin cuối). `loadUntil(id)`: tải thêm lịch sử tới tin `id` (mở kết quả tìm kiếm / tin gốc
 * cũ hơn 200 tin cuối) — giữ nguyên cho tới khi đổi hội thoại.
 */
/** Số tin mỗi lần nạp / mỗi lần "tải thêm". Khóa localStorage `swchat.messages-page` chỉ để kiểm thử UI với trang nhỏ. */
export const MESSAGES_PAGE = (() => {
  try {
    const n = Number(localStorage.getItem('swchat.messages-page'));
    return Number.isInteger(n) && n >= 5 && n <= 1000 ? n : 200;
  } catch {
    return 200;
  }
})();

export function useMessages(
  conversationId: string | undefined,
): [LocalMessage[], (messageId: string) => void, { hasMore: boolean; loadMore: () => void }] {
  const client = useClient();
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [anchor, setAnchor] = useState<{ conv: string; id: string }>();
  const [paging, setPaging] = useState<{ conv: string; limit: number }>();
  const includeId = anchor && anchor.conv === conversationId ? anchor.id : undefined;
  const limit = paging && paging.conv === conversationId ? paging.limit : MESSAGES_PAGE;
  useEffect(() => {
    if (!conversationId) return setMessages([]);
    let alive = true;
    const reload = () =>
      void client.listMessages(conversationId, limit, { includeId }).then((list) => alive && setMessages(list));
    reload();
    const off = client.on('messages', (id) => id === conversationId && reload());
    return () => {
      alive = false;
      off();
    };
  }, [client, conversationId, includeId, limit]);
  const loadUntil = useCallback(
    (messageId: string) => conversationId && setAnchor({ conv: conversationId, id: messageId }),
    [conversationId],
  );
  // Hết lịch sử khi số tin trả về chưa đủ `limit`.
  const hasMore = !!conversationId && messages.length >= limit;
  const loadMore = useCallback(
    () => conversationId && setPaging({ conv: conversationId, limit: limit + MESSAGES_PAGE }),
    [conversationId, limit],
  );
  return [messages, loadUntil, { hasMore, loadMore }];
}

/**
 * Dữ liệu cục bộ của hội thoại tải lại khi có tin/hoạt động mới (`messages`, `activity`): ghim (W2.1), luồng (W2.2),
 * hoạt động (W2.4). `load` phải ổn định theo `key` (đổi `key` → tải lại từ đầu).
 */
function useConversationData<T>(conversationId: string | undefined, key: string | undefined, initial: T, load: (c: SwChatClient) => Promise<T>): T {
  const client = useClient();
  const [data, setData] = useState<T>(initial);
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    setData(initial);
    if (!conversationId || key === undefined) return;
    let alive = true;
    let n = 0;
    const reload = () => {
      const mine = ++n;
      void loader
        .current(client)
        .then((v) => alive && mine === n && setData(v))
        .catch(() => undefined);
    };
    reload();
    const offs = [
      client.on('messages', (id) => id === conversationId && reload()),
      client.on('activity', (id) => id === conversationId && reload()),
    ];
    return () => {
      alive = false;
      offs.forEach((off) => off());
    };
    // `initial` chỉ là giá trị đầu — không theo dõi.
  }, [client, conversationId, key]);
  return data;
}

const NONE: never[] = [];

/** W2.1: tin đang ghim của hội thoại. */
export function usePinned(conversationId: string | undefined): PinnedMessage[] {
  return useConversationData(conversationId, conversationId, NONE as PinnedMessage[], (c) => c.getPinned(conversationId!));
}

/** W2.2: các luồng của hội thoại (số phản hồi, giờ cuối). */
export function useThreads(conversationId: string | undefined): ThreadSummary[] {
  return useConversationData(conversationId, conversationId, NONE as ThreadSummary[], (c) => c.threadSummaries(conversationId!));
}

/** W2.2: phản hồi trong luồng `rootId` (undefined: không mở luồng nào). */
export function useThread(conversationId: string | undefined, rootId: string | undefined): LocalMessage[] {
  return useConversationData(conversationId, rootId, NONE as LocalMessage[], (c) => c.listThread(conversationId!, rootId!));
}

/** W2.3: tin file của hội thoại (gồm file trong luồng), mới nhất ở đầu. */
export function useFiles(conversationId: string | undefined): LocalMessage[] {
  return useConversationData(conversationId, conversationId, NONE as LocalMessage[], (c) => c.listFiles(conversationId!));
}

/** W2.4: nhật ký hoạt động cục bộ của hội thoại (cũ nhất ở đầu). */
export function useActivity(conversationId: string | undefined): LocalActivity[] {
  return useConversationData(conversationId, conversationId, NONE as LocalActivity[], (c) => c.getActivity(conversationId!));
}

/** Người đang gõ trong hội thoại (tự hết sau 4 giây). */
export function useTyping(conversationId: string | undefined): string[] {
  const client = useClient();
  const [typing, setTyping] = useState<Record<string, number>>({});
  useEffect(() => {
    setTyping({});
    if (!conversationId) return;
    const off = client.on('typing', (conv, user) => {
      if (conv === conversationId) setTyping((t) => ({ ...t, [user]: Date.now() }));
    });
    const timer = setInterval(() => setTyping((t) => Object.fromEntries(Object.entries(t).filter(([, at]) => Date.now() - at < 4000))), 1000);
    return () => {
      off();
      clearInterval(timer);
    };
  }, [client, conversationId]);
  return Object.keys(typing);
}

/** Receipt theo người của hội thoại (cập nhật khi có tin/receipt mới). */
export function useReceipts(conversationId: string | undefined): Record<string, ReceiptState> {
  const client = useClient();
  const [receipts, setReceipts] = useState<Record<string, ReceiptState>>({});
  useEffect(() => {
    if (!conversationId) return setReceipts({});
    const reload = () => void client.receipts(conversationId).then(setReceipts);
    reload();
    return client.on('messages', (id) => id === conversationId && reload());
  }, [client, conversationId]);
  return receipts;
}

/** E1: dự án của mình (server đồng bộ từ portal khi đăng nhập). Lỗi tải → danh sách trống. */
export function useProjects(): Project[] {
  const client = useClient();
  const [projects, setProjects] = useState<Project[]>([]);
  useEffect(() => {
    let alive = true;
    void client
      .getProjects()
      .then((list) => alive && setProjects(list))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client]);
  return projects;
}

/** Tab đang hiển thị (dùng để quyết định đánh dấu đã đọc / hiện thông báo). */
export function useVisible(): boolean {
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState === 'visible');
  // Trong iframe (nhúng portal) `visibilityState` là của tab portal: iframe bị ẩn (display:none) hoặc cuộn khỏi màn hình
  // vẫn "visible" → kiểm thêm iframe có giao với viewport trang cha không (IntersectionObserver chạy được khác origin).
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const onChange = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    window.addEventListener('focus', onChange);
    let observer: IntersectionObserver | undefined;
    if (window.self !== window.top && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver((entries) => {
        const last = entries.at(-1);
        if (last) setInView(last.isIntersecting && last.intersectionRect.height > 0);
      });
      observer.observe(document.documentElement);
    }
    return () => {
      document.removeEventListener('visibilitychange', onChange);
      window.removeEventListener('focus', onChange);
      observer?.disconnect();
    };
  }, []);
  return tabVisible && inView;
}

/**
 * Tùy chọn cá nhân (nhãn, nhóm, ghim, highlight, cài đặt) đã giải mã. Tải lần đầu khi mount; cập nhật tức thì khi
 * chính mình sửa (ghi lạc quan) hoặc thiết bị khác của cùng tài khoản đổi.
 */
export function usePrefs(): PersonalPrefs {
  const client = useClient();
  const [prefs, setPrefs] = useState(() => client.prefs);
  useEffect(() => {
    const sync = () => setPrefs(client.prefs);
    const off = client.on('prefs', sync);
    void client.loadPrefs().then(sync, () => undefined);
    return off;
  }, [client]);
  return prefs;
}

/** Kênh thông báo của người dùng (nhóm, chưa đọc, ghim, quyền); tải lại khi có thông báo mới hoặc kênh đổi. */
export function useChannels(): { channels: Channel[]; canManage: boolean; reload: () => void } {
  const client = useClient();
  const [state, setState] = useState<{ channels: Channel[]; canManage: boolean }>({ channels: [], canManage: false });
  const reload = useCallback(() => {
    void client.api.announcementChannels().then(
      (r) => setState({ channels: r.channels, canManage: r.can_manage }),
      () => undefined,
    );
  }, [client]);
  useEffect(() => {
    reload();
    return client.on('announcement', reload);
  }, [client, reload]);
  return { ...state, reload };
}

/** Số thông báo công ty chưa đọc + quyền gửi; tải lại khi có thông báo mới. */
export function useAnnouncementSummary(): { unread: number; canSend: boolean; reload: () => void } {
  const client = useClient();
  const [state, setState] = useState({ unread: 0, canSend: false });
  const reload = useCallback(() => {
    void client.announcements(undefined, 1).then(
      (r) => setState({ unread: r.unread, canSend: r.can_send }),
      () => undefined,
    );
  }, [client]);
  useEffect(() => {
    reload();
    return client.on('announcement', reload);
  }, [client, reload]);
  return { ...state, reload };
}

/**
 * Hàm tra ảnh đại diện theo id (người dùng, bot hoặc hội thoại) cho `AvatarSrcProvider`. Cập nhật khi danh bạ hoặc ảnh
 * nhóm đổi (sự kiện `users` / `conversations`).
 */
export function useAvatarLookup(conversations: readonly { id: string; avatar?: string }[]): (seed: string) => string | undefined {
  const client = useClient();
  const [version, setVersion] = useState(0);
  useEffect(() => client.on('users', () => setVersion((n) => n + 1)), [client]);
  return useCallback(
    (seed: string) => client.getUser(seed)?.avatar ?? conversations.find((c) => c.id === seed)?.avatar,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, conversations, version],
  );
}
